import React from 'react';
import { Container, Text, Spacer, Card, Row, Col, Link } from "@nextui-org/react";
import { useAuth } from '../../context/AuthContext';

type ConfigLink = {
  label: string;
  href: string;
};

type ConfigSection = {
  title: string;
  rows: ConfigLink[][];
};

const getConfigurationData = (projectId: string): ConfigSection[] => {
  // Try to remove standard suffix if any, or just assume the admin project is `${projectId}-admin` if dev-aiproto-studio
  // The user probably wants it fully dynamic:
  const adminProjectId = projectId.endsWith('-admin') ? projectId : `${projectId}-admin`;
  const baseProjectId = projectId.replace('-admin', '');

  return [
    {
      title: "GCP",
      rows: [
        [{ label: "Secret Management", href: `https://console.cloud.google.com/welcome?project=${projectId}` }],
        [{ label: "Firebase Admin (App)", href: `https://console.firebase.google.com/u/0/project/${baseProjectId}/` }],
        [{ label: "Firebase Admin (Admin)", href: `https://console.firebase.google.com/u/0/project/${adminProjectId}/hosting/sites/${adminProjectId}` }],
        [{ label: "Secret Manager", href: `https://console.cloud.google.com/security/secret-manager?project=${projectId}` }],
        [{ label: "Cloud Tasks", href: `https://console.cloud.google.com/cloudtasks/queue/us-central1/handleLkEngressTimeout/tasks?authuser=0&cloudshell=true&hl=pt-BR&project=${projectId}` }],
        [{ label: "Cloud Functions (overview)", href: `https://console.cloud.google.com/run/overview?project=${projectId}&authuser=0&cloudshell=true&hl=pt-BR` }]
      ]
    },
    {
      title: "GitHub",
      rows: [
        [{ label: "Env Variables", href: "https://github.com/vsmori/phd-frontend-02/settings/environments/10753334294/edit" }]
      ]
    },
    {
      title: "MUX",
      rows: [
        [
          { label: "EgressClient", href: "https://docs.livekit.io/reference/server-sdk-js/classes/EgressClient.html" },
          { label: "EgressInfo", href: "https://docs.livekit.io/reference/server-sdk-js/classes/EgressInfo.html" }
        ]
      ]
    },
    {
      title: "HyperBeam",
      rows: [
        [
          { label: "Docs", href: "https://docs.hyperbeam.com/rest-api/dispatch/start-chromium-session" },
          { label: "Dashboard", href: "https://hyperbeam.com/dashboard/" },
          { label: "Discord", href: "https://discord.com/channels/966073020336734308/1020384881118814218/threads/1479191565913292935" }
        ]
      ]
    }
  ];
};

const ConfigurationsPage: React.FC = () => {
  const { config } = useAuth();

  // The API may return the project ID in different formats depending on how the endpoint wraps it.
  const resolvedProjectId = config?.projectId || config?.data?.projectId || config?.result?.projectId || config?.project_id;
  const projectId = resolvedProjectId || "dev-aiproto-studio";

  // ensure it reacts to config changes explicitly
  const configurationData = React.useMemo(() => getConfigurationData(projectId), [projectId]);

  return (
    <Container>
      <Spacer y={1} />
      <Text h2>Configurations</Text>
      <Spacer y={1} />

      {configurationData.map((section, index) => (
        <React.Fragment key={index}>
          <Card variant="bordered" css={{ mw: "400px" }}>
            <Card.Header>
              <Text b size={18}>{section.title}</Text>
            </Card.Header>
            <Card.Divider />
            <Card.Body>
              {section.rows.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <Row>
                    {row.map((link, colIndex) => (
                      <Col key={colIndex}>
                        <Link href={link.href} target="_blank" rel="noopener noreferrer">
                          {link.label}
                        </Link>
                      </Col>
                    ))}
                  </Row>
                  {rowIndex < section.rows.length - 1 && <Spacer y={0.5} />}
                </React.Fragment>
              ))}
            </Card.Body>
          </Card>
          {index < configurationData.length - 1 && <Spacer y={1} />}
        </React.Fragment>
      ))}
    </Container>
  );
};

export default ConfigurationsPage;
